import { useMemo, useState } from 'react'
import { YStack, XStack, FinancialTable, SimpleText, ActiveButton, useToastController, DataLoadingDialog, Spinner, ActiveDropdown, ScrollView, ActionButton } from '@my/ui'
import {
  color,
  useAuthedRequestFn,
  convertAndFilterData,
  transformFinancialPlanData,
  convertToFinancialPlanSummary,
  MiscUtils,
} from '@my/util'

import {
  ToggleButtonGroup,
  FinancialReviewHeading,
  ExportDropdown,
  TaskDetailModal,
} from '../../../components/index'
import { TaskProgressList } from './progressReport'
import { FinancialPlanRoadMap } from './financialPlanRoadMap'
import { MilestonesList } from './keyMileStones'
import { FinancialSummary, PriorityBadge } from './financialSummaryCard'
import images from '../../../assets/images'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ActionComponent } from './taskActionButton'
import { TZASummaryCards } from '@my/ui'
import { Platform } from 'react-native'
import { CircleX, Info } from '@tamagui/lucide-icons-2'

// Constants
const DEFAULT_TABLE_HEADINGS = [
  { key: "category", text: 'Task Name' },
  { key: "description", text: 'Linked Goal' },
  { key: 'action', text: 'Due Date' },
  { key: 'status', text: 'Status' },
  { key: "Mark as Done", text: 'Completed' },
  { key: 'actionButtons', text: 'Actions' },
]

const financialData = [
  { title: 'Previous Month', description: 'Previous Actions' },
  {
    title: 'November 2024',
    description: 'Emergency Fund',
    timeline: 'Due Dec 15',
    tag: 'Current',
    task: 'Set up automatic transfer',
  },
  { title: 'Next Month', description: 'Future Actions' },
]

const HeaderComponent = ({
  subHeader,
  header,
  onPress,
  buttonText,
  invertButton
}: {
  subHeader?: string
  header: string
  onPress?: (data) => void
  buttonText: String
  invertButton: boolean
}) => (
  <XStack flexWrap="wrap" justifyContent="space-between" gap={10}>
    <YStack gap={10}>
      <SimpleText fontFamily="$heading" color={color.grey} fontSize="$6" fontWeight="bold">
        {header}
      </SimpleText>
      {subHeader && <SimpleText color={color.grey}>{subHeader}</SimpleText>}
    </YStack>
    <XStack justifyContent="flex-end">
      <ActiveButton
        style={{width: 105}}
        onPress={onPress}
        ButtonTextProps={{children: buttonText}}
        {...(invertButton ? {variant: "outlined"} : {})}
      />
    </XStack>
  </XStack>
)


const PeriodToggle = ({ active, setActive, options = [''], header, toggleButton }) => (
  <XStack justifyContent="space-between" flexWrap="wrap" alignItems="center" gap={10}>
    <SimpleText fontWeight="800" color={color.darkNavy} fontSize={14}>
      {header}
    </SimpleText>
    {toggleButton ? (
      <ToggleButtonGroup active={active} onChange={setActive} options={options} />
    ) : (

      <ActiveDropdown
        items={[
          { label: '1 month', value: '1 month' },
          { label: '3 months', value: '3 months' },
          { label: '6 months', value: '6 months' },
        ]}
        val={active}
        setVal={setActive}
        containerStyle={[{width: 100}]}
      />
      /* <ExportDropdown
        showIcon={false}
        onExport={(key) => setActivePeriod(key)}
        header={activePeriod}
        list={[
          { key: '1 month', value: '1 month' },
          { key: '3 months', value: '3 months' },
          { key: '6 months', value: '6 months' },
        ]}
      /> */
    )}
  </XStack>
)

// Main Component
export const FinancialPlan = () => {
  const authedRequestFn = useAuthedRequestFn()
  const queryClient = useQueryClient()
  const [formSubmitting, setFormSubmitting] = useState(false)
  const [showNotice, setNotice] = useState(true)
  const toast = useToastController()

  const { data, isLoading } = useQuery({
    queryKey: ['financial-plan'],
    queryFn: () => authedRequestFn('/financial-plan'),
  })
  
  const editMutation = useMutation({
    mutationFn: ({data, method = 'PUT'}:{data: any, method?: 'PUT' | 'DELETE'}) => 
      authedRequestFn('/financial-plan/monthly-action', { method, body: data }),
    onMutate: () => setFormSubmitting(true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-plan'] })
      toast.show('Successfully saved!')
    },
    onSettled: () => {
      setFormSubmitting(false)
      //@ts-ignore
      //goBack ? goBack.onPress() : goWeb()
    },
  })

  const [activePeriod, setActivePeriod] = useState<'1 month' | '3 months' | '6 months'>('3 months')
  const [timeline, setTimeline] = useState<'All Tasks' | 'Due Soon'>('All Tasks')
  const [milestone, setMilestone] = useState<boolean>(true)

  const financialPlanData = useMemo(
    () => transformFinancialPlanData(data?.monthlyActionItems, {
      markAsDone: (actionItem) => editMutation.mutateAsync({data: actionItem}),
      edit: (actionItem) => Promise.resolve(),
      delete: async (actionItem) => {
        const result = await MiscUtils.dialog.confirm("Are you sure you want to continue?", "Deleting a Financial Plan Action Item cannot be undone.");
        if (result) {
          return editMutation.mutateAsync({data: actionItem, method: 'DELETE'});
        } else {
          return Promise.resolve();
        }
      },
    }),
    [data?.monthlyActionItems]
  ) // Monthly Action Table
  const financialPlanSummary =  useMemo(
    () => convertToFinancialPlanSummary(data?.cardsUI ?? {}),
    [data?.cardsUI]
  ) // Plan Summary
  const monthlyProgress = useMemo(
    () => convertAndFilterData(data?.monthlyProgress, activePeriod), // Monthly Progress
    [data?.monthlyProgress, activePeriod]
  )
  const milestones = milestone ? data?.milestones?.slice(0, 2) : data?.milestones;

  const onMilstonePress = () => setMilestone(prev => !prev)

  const mainContent = <>
      {/* <FinancialSummary listData={financialPlanSummary ?? []} /> */}
      <TZASummaryCards cards={financialPlanSummary.map(item => ({
        title: item.status,
        value: item.value,
        icon: <PriorityBadge priority={item.priority} time={item.time} />
      }))} />
      <PeriodToggle
        active={timeline}
        setActive={setTimeline}
        options={['All Tasks', 'Due Soon']}
        header={'Monthly Action Items'}
        toggleButton={true}
      />
      {financialPlanData ? <FinancialTable
        data={(timeline == 'Due Soon' ? financialPlanData?.slice(0, 5) : financialPlanData) || []}
        headings={DEFAULT_TABLE_HEADINGS}
        columnWidth={190} 
        mobileRenderOptions={{
          titleKey: 'category',
          mainValueKeys: ['status', 'action'],
          interactiveKeys: ['actionButtons'],
          otherKeys: [["Mark as Done", 'description']],
          titleTextStyle: { fontSize: 16 }
        }}
      /> : null}
      <PeriodToggle
        active={activePeriod}
        setActive={setActivePeriod}
        header={'Monthly Progress'}
        toggleButton={false}
      />

      <TaskProgressList data={monthlyProgress ?? []} />
      {/* <HeaderComponent
        subHeader={'Your roadmap to achieving your financial goals.'}
        header={'FINANCIAL PLAN'}
        buttonText={'View Goal'}
      />
      <FinancialPlanRoadMap data={financialData} /> */}
      {Platform.select({native: null, web:
        <>
          <HeaderComponent
            header={'Key milestones'}
            buttonText={milestone ? 'View All' : 'Hide'}
            invertButton={!milestone}
            onPress={onMilstonePress}
          />
          <MilestonesList milestones={milestones ?? []} />
        </>
      })}
      {/* <XStack flexWrap="wrap" gap={20} justifyContent="space-between">
        <ActionComponent
          heading={'Updated Goal Progress'}
          sunHeading={'Track your achievements'}
          imageSrc={images.flag}
        />
        <ActionComponent
          heading={'Adjust Timeline'}
          sunHeading={'Modify your schedule'}
          imageSrc={images.calendar}
        />
      </XStack> */}
      {/* <TaskDetailModal modalVisible={false} handleSubmitForm={() => {}} onClose={() => {}} /> */}
  </>

  const output = (
    <YStack p="$4" backgroundColor="white" gap={20}>
      <FinancialReviewHeading
        title="Financial Plan"
        img={images.financialPlan}
        imageStyle={{
          '$platform-native': { width: 26, height: 22 },
          '$platform-web': { width: 24, height: 26 },
          resizeMode: 'contain',
        }}
      />
      {showNotice ? <XStack position="relative" gap={10} backgroundColor={'rgb(243, 245, 247)'} padding={'$4'} borderRadius={10} borderWidth={1} borderColor={'rgba(220, 224, 229, 0.6)'}>
        <ActionButton
          unstyled
          circular
          onPress={() => setNotice(false)}
          style={{ position: 'absolute', top: 2, right: 2 }}
          hoverStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
          pressStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
        >
          <CircleX />
        </ActionButton>
        <XStack mb={'$2'}>
          <Info width={20} height={20} />
        </XStack>
        <SimpleText fontSize={14} color={'rgb(102, 102, 102)'}>
          Your Financial Plan combines your goals with our expert recommendations and breaks them into simple monthly actions. No more wondering "what should I do next?" - we've got you covered.
        </SimpleText>
      </XStack> : null}
      {isLoading ? (
              <Spinner size="large" color="$green10" />
            ) : mainContent}
      <DataLoadingDialog open={formSubmitting} onOpenChange={setFormSubmitting} />
    </YStack>
  );
  
  return (
    Platform.OS === 'web' ? (
      output
    ) : (
      <ScrollView backgroundColor={color.white}>
        {output}
      </ScrollView>
    )
  );
}
